import React, { useState, useEffect } from "react";
import {
  fetchBoardCertifications,
  createBoardCertification,
  updateBoardCertification,
  deleteBoardCertification,
} from "../APIs/boardCertificationApi";

function BoardCertificationTab() {
  const [boardCerts, setBoardCerts] = useState([]);
  const [form, setForm] = useState({ id: null, name: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCertifications();
  }, []);

  async function loadCertifications() {
    try {
      setLoading(true);
      const data = await fetchBoardCertifications();
      setBoardCerts(data);
    } catch (err) {
      console.error("Failed to fetch certifications", err);
      alert("Failed to load board certifications.");
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }

    try {
      if (isEditing) {
        const updated = await updateBoardCertification(form.id, {
          name: form.name,
        });
        setBoardCerts((prev) =>
          prev.map((bc) => (bc.id === form.id ? updated : bc)),
        );
        setIsEditing(false);
      } else {
        const created = await createBoardCertification({ name: form.name });
        setBoardCerts((prev) => [...prev, created]);
      }

      setForm({ id: null, name: "" });
    } catch (err) {
      console.error("Failed to save certification", err);
      alert(err.response?.data?.message || "Error saving board certification.");
    }
  }

  function handleEdit(bc) {
    setForm(bc);
    setIsEditing(true);
  }

  async function handleDelete(id) {
    if (
      !window.confirm(
        "Are you sure you want to delete this board certification?",
      )
    )
      return;

    try {
      await deleteBoardCertification(id);
      setBoardCerts((prev) => prev.filter((bc) => bc.id !== id));
    } catch (err) {
      console.error("Failed to delete certification", err);
      alert("Error deleting board certification.");
    }
  }

  function handleCancel() {
    setForm({ id: null, name: "" });
    setIsEditing(false);
  }

  return (
    <div>
      <h2 className="text-xl font-semibold mb-3">Board Certifications</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 max-w-md bg-gray-50 p-4 rounded shadow"
      >
        <div className="mb-3">
          <label className="block font-medium mb-1" htmlFor="name">
            Name <span className="text-red-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={form.name}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
            required
          />
        </div>

        <div className="flex space-x-2">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            {isEditing ? "Update" : "Add"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <table className="min-w-full border border-gray-300 rounded shadow">
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-4 py-2 text-left">ID</th>
            <th className="border px-4 py-2 text-left">Name</th>
            <th className="border px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={3} className="text-center p-4 text-gray-500">
                Loading...
              </td>
            </tr>
          ) : boardCerts.length === 0 ? (
            <tr>
              <td colSpan={3} className="text-center p-4 text-gray-500">
                No board certifications found.
              </td>
            </tr>
          ) : (
            boardCerts.map((bc) => (
              <tr key={bc.id} className="hover:bg-gray-50">
                <td className="border px-4 py-2">{bc.id}</td>
                <td className="border px-4 py-2">{bc.name}</td>
                <td className="border px-4 py-2 text-center space-x-2">
                  <button
                    onClick={() => handleEdit(bc)}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(bc.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default BoardCertificationTab;
